import { useEffect, useState } from 'react';
import { api, sizeLabel, usd } from '../lib/api';
import { ErrorNote, useLoad } from '../components/ui';
import NewItemButton from '../components/NewItemButton';

export const KEY_COLORS: Record<string, string> = { gray: '#e5e7eb', red: '#fecaca', orange: '#fed7aa', yellow: '#fef08a', green: '#bbf7d0', blue: '#bfdbfe', purple: '#e9d5ff' };

// Quick keys: the big buttons on the register for things you sell all day, so nobody has to scan or search.
export default function RegisterLayout() {
  const saved = useLoad(() => api<{ keys: any[] }>('/api/register-layout'), []);
  const [keys, setKeys] = useState<any[]>([]);
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [note, setNote] = useState('');
  const [find, setFind] = useState('');
  const found = useLoad(() => (find.length >= 2 ? api<any[]>(`/api/items?q=${encodeURIComponent(find)}&limit=8`) : Promise.resolve([])), [find]);
  useEffect(() => { if (saved.data) { setKeys(saved.data.keys); setDirty(false); } }, [saved.data]);
  const change = (next: any[]) => { setKeys(next); setDirty(true); setNote(''); };
  const move = (i: number, d: number) => { const j = i + d; if (j < 0 || j >= keys.length) return; const c = [...keys]; [c[i], c[j]] = [c[j]!, c[i]!]; change(c); };
  const patch = (i: number, p: object) => change(keys.map((k, j) => (j === i ? { ...k, ...p } : k)));
  const add = (item: any) => {
    if (keys.some((k) => k.itemId === item.id)) return;
    change([...keys, { itemId: item.id, label: `${item.brand} ${item.name}`.slice(0, 24), color: null, retail_cents: item.retail_cents, brand: item.brand, name: item.name, size_ml: item.size_ml }]);
    setFind('');
  };
  async function suggest() {
    setErr(null);
    try {
      const r = await api<{ keys: any[] }>('/api/register-layout/suggest');
      if (r.keys.length === 0) { setNote('Nothing has sold in the last 90 days yet, so there is nothing to suggest.'); return; }
      if (keys.length && !confirm('Replace the current layout with your best sellers?')) return;
      change(r.keys);
    } catch (e) { setErr(e); }
  }
  async function save() {
    setErr(null);
    try {
      await api('/api/register-layout', { method: 'PUT', body: { keys: keys.map((k) => ({ itemId: k.itemId, label: k.label, color: k.color })) } });
      setNote('Saved. Registers pick it up the next time the page loads.'); setDirty(false); saved.reload();
    } catch (e) { setErr(e); }
  }
  return (
    <div className="stack">
      <div className="page-head">
        <div className="grow"><h1>Register buttons</h1><p>Pick the items you ring up most and they show as buttons on the register. Tap one to add it to the sale. Use the arrows to put them in order, and a color to make a group easy to find.</p></div>
        <button onClick={suggest}>Fill from best sellers</button>
        <button className="primary" disabled={!dirty} onClick={save}>Save layout</button>
      </div>
      <ErrorNote error={saved.error ?? err} />
      {note && <div className="notice good">{note}</div>}
      <div className="panel"><div className="panel-body stack">
        <label>Add an item<input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Search your items" /></label>
        {found.data?.map((i) => <button key={i.id} className="ghost" style={{ display: 'block', width: '100%', textAlign: 'left' }} onClick={() => add(i)}>{i.brand} {i.name} · {sizeLabel(i.size_ml)} · {usd(i.retail_cents)}</button>)}
        <div><NewItemButton onCreated={(i) => add(i)} /></div>
      </div></div>
      <div className="panel"><div className="panel-body">
        {keys.length === 0 && <div className="empty">No buttons yet. Search for an item above, or fill from your best sellers.</div>}
        <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))' }}>
          {keys.map((k, i) => (
            <div key={k.itemId} className="panel" style={{ background: KEY_COLORS[k.color ?? 'gray'], padding: 8 }}>
              <input aria-label="Button text" maxLength={24} value={k.label} onChange={(e) => patch(i, { label: e.target.value })} />
              <div className="faint" style={{ margin: '4px 0' }}>{k.brand} {k.name} · {sizeLabel(k.size_ml)} · {usd(k.retail_cents)}</div>
              <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                <select aria-label="Color" value={k.color ?? 'gray'} onChange={(e) => patch(i, { color: e.target.value === 'gray' ? null : e.target.value })}>{Object.keys(KEY_COLORS).map((c) => <option key={c} value={c}>{c}</option>)}</select>
                <button aria-label="Move earlier" disabled={i === 0} onClick={() => move(i, -1)}>←</button>
                <button aria-label="Move later" disabled={i === keys.length - 1} onClick={() => move(i, 1)}>→</button>
                <button className="danger" aria-label="Remove button" onClick={() => change(keys.filter((_, j) => j !== i))}>✕</button>
              </div>
            </div>))}
        </div>
      </div></div>
    </div>
  );
}
